import React from "react";
import { Gavel, FileCheck, Archive, BookOpen, Megaphone, ArrowRight, ShieldAlert } from "lucide-react";
import { useI18n } from "@/i18n/I18nProvider";
import { Btn, Eyebrow, Section, SectionTitle, Lead } from "@/components/ui-kit/Kit";
import { Reveal, Stagger, StaggerItem } from "@/components/motion/Reveal";
import { IMAGES } from "@/config/images";

const ICONS = { gavel: Gavel, "file-check": FileCheck, archive: Archive, "book-open": BookOpen, megaphone: Megaphone };

export const Shield = () => {
  const { t } = useI18n();
  const s = t("home.shield");

  return (
    <Section className="py-24 sm:py-32" data-testid="shield-section">
      <div className="grid gap-14 lg:grid-cols-[0.9fr_1.1fr] lg:gap-20">
        <div>
          <Reveal>
            <Eyebrow>{s.eyebrow}</Eyebrow>
            <SectionTitle className="mt-6 max-w-[16ch]">{s.title}</SectionTitle>
            <Lead className="mt-5 max-w-md">{s.sub}</Lead>
          </Reveal>

          <Reveal delay={0.15}>
            <div className="relative mt-10 overflow-hidden">
              <img
                src={IMAGES.compliance.src}
                alt={IMAGES.compliance.alt}
                loading="lazy"
                className="aspect-[4/3] w-full object-cover"
              />
              <div
                aria-hidden="true"
                className="absolute inset-0 bg-[radial-gradient(circle_at_30%_30%,transparent_0%,rgba(10,25,47,0.72)_100%)]"
              />
              <div className="absolute bottom-0 left-0 flex items-center gap-3 bg-navy px-5 py-3">
                <ShieldAlert className="h-4 w-4 text-gold" />
                <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-white/80">{s.note}</span>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.2}>
            <Btn to="/gpu-protection" variant="navy" className="mt-9" data-testid="explore-protection">
              {t("cta.exploreProtection")} <ArrowRight className="h-4 w-4" />
            </Btn>
          </Reveal>
        </div>

        <Stagger className="divide-y divide-navy/10 border-y border-navy/10">
          {(s.items || []).map((it, i) => {
            const Icon = ICONS[it.icon] || Gavel;
            return (
              <StaggerItem key={it.title}>
                <div
                  className="group flex gap-6 py-7 transition-[padding] duration-500 hover:pl-3"
                  data-testid={`shield-item-${i + 1}`}
                >
                  <Icon className="mt-1 h-6 w-6 shrink-0 text-navy/40 transition-colors duration-500 group-hover:text-grn" strokeWidth={1.4} />
                  <div>
                    <h3 className="font-display text-lg font-semibold text-navy">{it.title}</h3>
                    <p className="mt-2 text-sm leading-relaxed text-slate-600">{it.body}</p>
                  </div>
                  <span className="ml-auto self-center font-mono text-[10px] text-navy/20">
                    {String(i + 1).padStart(2, "0")}
                  </span>
                </div>
              </StaggerItem>
            );
          })}
        </Stagger>
      </div>
    </Section>
  );
};
